import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { LanguageSelector } from "../LanguageSelector";
import { useLanguage } from "../../context/LanguageContext";

export const Navbar = () => {
  const { content } = useLanguage();
  const ui = content.ui;
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: ui.nav.home },
    { to: "/matieres-premieres", label: ui.nav.commodities },
    { to: "/a-propos", label: ui.nav.about },
    { to: "/expertise", label: ui.nav.expertise },
    { to: "/pourquoi-nous", label: ui.nav.why },
    { to: "/exportation", label: ui.nav.export },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 z-50 w-full transition-colors duration-500 ${
        scrolled
          ? "border-b border-black/5 bg-white/80 backdrop-blur-xl"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <Link
          to="/"
          data-testid="navbar-brand"
          className={`font-serif text-xl tracking-tight transition-colors ${
            scrolled ? "text-brand-green" : "text-white"
          }`}
        >
          Lauréate <span className="text-brand-gold">Commodity</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.15em] transition-opacity duration-300 hover:opacity-60 ${
                  scrolled ? "text-brand-black" : "text-white"
                } ${isActive ? "opacity-100" : "opacity-80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <LanguageSelector dark={!scrolled} />
          <Link
            to="/contact"
            data-testid="navbar-contact-cta"
            className={`hidden rounded-none border px-6 py-2.5 text-xs uppercase tracking-[0.15em] transition-colors duration-300 md:inline-block ${
              scrolled
                ? "border-brand-green text-brand-green hover:bg-brand-green hover:text-white"
                : "border-white text-white hover:bg-white hover:text-brand-green"
            }`}
          >
            {ui.nav.contact}
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Menu"
                className={`lg:hidden ${scrolled ? "text-brand-green" : "text-white"}`}
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85%] border-none bg-brand-green text-white sm:w-[380px]"
              data-testid="mobile-menu"
            >
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <div className="mt-12 flex flex-col gap-6">
                {[...links, { to: "/contact", label: ui.nav.contact }].map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="font-serif text-3xl text-white/90 transition-colors hover:text-brand-gold"
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
